import React, { useEffect, useMemo, useRef, useState } from 'react'
import api from 'api/api'
import { confirmAsync } from 'components/shared/confirmUtils'
import Tray from 'components/shared/Tray'
import { useTemporalWorkflowStatus } from 'hooks/useTemporalWorkflowStatus'
import type { CombinedOrdinal } from './OrdinalsViewer'
import styles from './send-ordinal.module.scss'
import { Loader2, Send } from 'lucide-react'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'
import { getClientShare } from 'lib/bsv-signing'
import { treechatSignsWithoutLocalKey } from 'lib/clientSigningFeature'
import {
    clearOrdinalSendIntent,
    saveOrdinalSendIntent,
    type OrdinalSendIntent,
} from 'lib/ordinal-send-intent'

type Props = {
    ordinal: CombinedOrdinal
    onDismiss: () => void
    onRefreshOrdinals: () => void
}

export default function SendOrdinal({ ordinal, onDismiss, onRefreshOrdinals }: Props) {
    const isSelfCustodyUser = useUserHasFeature('use_client_signing')
    const [recipientAddress, setRecipientAddress] = useState('')
    const [sendWorkflowId, setSendWorkflowId] = useState<string | null>(null)
    const [sendRunId, setSendRunId] = useState<string | null>(null)
    const [sendAccessToken, setSendAccessToken] = useState<string | null>(null)
    const [isSending, setIsSending] = useState(false)
    const pollingWorkflowKey = useRef<string | null>(null)
    const activeSendIntent = useRef<OrdinalSendIntent | null>(null)

    const clearActiveSendIntent = () => {
        const intent = activeSendIntent.current
        if (!intent) return
        clearOrdinalSendIntent(intent.ordinalId, intent.recipientAddress, intent.ordinalOutpoint)
        activeSendIntent.current = null
    }

    const canSend = useMemo(() => {
        return !!ordinal.treechatData?.id && !!recipientAddress.trim() && !isSending
    }, [ordinal.treechatData?.id, recipientAddress, isSending])

    const { startPolling } = useTemporalWorkflowStatus(
        sendWorkflowId,
        sendRunId,
        'progress',
        'AppTemporal::Workflows::SendOrdinalWorkflow',
        {
            enabled: !!sendWorkflowId && !!sendRunId && !!sendAccessToken,
            eventBusTopic: 'send_ordinal_status',
            logPrefix: 'Sending',
            accessToken: sendAccessToken || undefined,
            onComplete: async rawResult => {
                const result = rawResult as unknown as Record<string, unknown>
                const statusMessages = Array.isArray(result?.result) ? result.result : []
                const lastStatus = (statusMessages[statusMessages.length - 1] || {}) as Record<
                    string,
                    unknown
                >

                if (lastStatus?.error === true || result?.error) return

                const txId =
                    lastStatus?.tx_id || result?.tx_id || result?.txid || result?.transactionId

                if (txId) {
                    const message = `Ordinal sent successfully!\n\nTransaction ID: ${txId}`
                    const confRes = await confirmAsync(message, {
                        confirmLabel: 'View Transaction',
                        cancelMessage: 'Close',
                        hideCancel: false,
                        iconType: 'success',
                    })
                    if (confRes) window.open(`https://whatsonchain.com/tx/${txId}`, '_blank')
                } else {
                    await confirmAsync('Ordinal sent successfully!', {
                        confirmLabel: 'OK',
                        hideCancel: true,
                        iconType: 'success',
                    })
                }

                setIsSending(false)
                clearActiveSendIntent()
                setRecipientAddress('')
                setSendWorkflowId(null)
                setSendRunId(null)
                setSendAccessToken(null)

                onRefreshOrdinals()
                onDismiss()
            },
            onError: async (error: string) => {
                const defaultMessage = 'Failed to send ordinal. Please try again.'
                const rawMessage = error
                const errorMessage = rawMessage ? (
                    <div>
                        <div>{defaultMessage}</div>
                        <div style={{ marginTop: '12px' }}>{rawMessage}</div>
                    </div>
                ) : (
                    defaultMessage
                )

                await confirmAsync(errorMessage, {
                    confirmLabel: 'OK',
                    hideCancel: true,
                    iconType: 'error',
                })
                setIsSending(false)
                setSendWorkflowId(null)
                setSendRunId(null)
                setSendAccessToken(null)
                clearActiveSendIntent()
            },
        },
    )

    useEffect(() => {
        if (!sendWorkflowId || !sendRunId || !sendAccessToken) {
            pollingWorkflowKey.current = null
            return
        }

        const workflowKey = `${sendWorkflowId}:${sendRunId}`
        if (pollingWorkflowKey.current === workflowKey) return

        pollingWorkflowKey.current = workflowKey
        startPolling()
    }, [sendAccessToken, sendWorkflowId, sendRunId, startPolling])

    const handleSend = async () => {
        if (!ordinal.treechatData?.id) return
        if (!recipientAddress.trim()) return

        try {
            setIsSending(true)
            let deviceId: string | undefined
            const userId = gon.currentUser?.id
            const clientShare = isSelfCustodyUser && userId ? await getClientShare(userId) : null
            if (isSelfCustodyUser && !clientShare && !treechatSignsWithoutLocalKey())
                throw new Error('This device has no self-custody wallet. Set it up before sending.')
            // Without the local key a promoted user's send goes with no device id; Treechat signs.
            if (clientShare) {
                deviceId = clientShare.deviceId
                const ordinalOutpoint =
                    ordinal.descriptor?.status === 'resolved'
                        ? ordinal.descriptor.outpoint
                        : ordinal.utxo_outpoint || ordinal.outpoint
                const intent: OrdinalSendIntent = {
                    userId: String(userId),
                    deviceId,
                    ordinalId: ordinal.treechatData.id,
                    recipientAddress: recipientAddress.trim(),
                    ordinalOutpoint,
                    createdAt: Date.now(),
                }
                saveOrdinalSendIntent(intent)
                activeSendIntent.current = intent
            }
            const response = await api.sendOrdinalTemporal(
                ordinal.treechatData.id,
                recipientAddress.trim(),
                {
                    broadcast: true,
                    device_id: deviceId,
                },
            )
            if (response.workflow_id && response.run_id && response.access_token) {
                setSendWorkflowId(response.workflow_id)
                setSendRunId(response.run_id)
                setSendAccessToken(response.access_token)
            } else {
                throw new Error(
                    'Failed to start send workflow: missing workflow status credentials',
                )
            }
        } catch (error: unknown) {
            setIsSending(false)
            const status = (error as Error & { status?: number })?.status
            if (status && status >= 400 && status < 500) clearActiveSendIntent()
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initiate send. Please try again.'
            await confirmAsync(errorMessage, {
                confirmLabel: 'OK',
                hideCancel: true,
                iconType: 'error',
            })
        }
    }

    return (
        <Tray
            title="Send Ordinal"
            onDismiss={onDismiss}
            closeLabel="Close send"
            dialogTestId="send-ordinal-dialog"
        >
            <div className={styles.helper}>
                Enter a recipient BSV address to transfer this ordinal.
            </div>

            <label className={styles.label}>Recipient address</label>
            <input
                className={styles.input}
                data-testid="send-ordinal-recipient-input"
                value={recipientAddress}
                onChange={e => setRecipientAddress(e.target.value)}
                placeholder="1..."
                disabled={isSending}
            />

            <div className={styles.actions}>
                <button className={styles.secondaryButton} onClick={onDismiss} disabled={isSending}>
                    Cancel
                </button>
                <button
                    className={styles.primaryButton}
                    data-testid="send-ordinal-submit-button"
                    onClick={handleSend}
                    disabled={!canSend}
                >
                    {isSending ? (
                        <>
                            <Loader2 size={16} className={styles.spin} />
                            Sending…
                        </>
                    ) : (
                        <>
                            <Send size={16} />
                            Send
                        </>
                    )}
                </button>
            </div>
        </Tray>
    )
}
